import * as React from 'react';
import { dismissModal, getOpenModal, openTaxExemptModal } from '@/redux/modules/modal';
import { getCertificatesHaveLoaded, getHasPendingOrCompleteCertificate } from '@/redux/modules/certCapture';
import { getIsReseller, sendResellerType } from '@/redux/modules/reseller';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import checkForSkippablePage from './helpers/checkForSkippablePage';
import { FormattedMessage } from 'react-intl';
import { getTaxCertManagementUrl } from '@/utils/urls';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import Modal from '@liveauctioneers/caterwaul-components/lib/Modal/Modal';
import ModalHeader from '@liveauctioneers/caterwaul-components/lib/Modal/ModalHeader';
import { modalTypes } from '@/enums';
import { parseQueryParams } from '@/utils/queryParams';
import { push } from 'connected-react-router';
import styled from 'styled-components';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { useLocation } from 'react-router-dom';

// treat going to the tax exempt page as if they had seen the modal
const isTaxExemptPage = (path: string = '', tab: string) =>
    path.includes('dashboard/profile') && tab === 'tax-cert-management';

type QueryParams = {
    tab: string;
};

const TaxExemptModal = () => {
    const dispatch = useAppDispatch();
    const { pathname, search } = useLocation();
    const { tab } = parseQueryParams<QueryParams>(search);
    const taxExemptPage = isTaxExemptPage(pathname, tab);
    const isSkippablePage = checkForSkippablePage(pathname);

    const [modalPopped, setModalPopped] = React.useState(false);
    const isReseller = useAppSelector(getIsReseller);
    const isModalOpen = useAppSelector(getOpenModal) === modalTypes.TAX_EXEMPT;
    const hasValidTaxExemptCertificate = useAppSelector(getHasPendingOrCompleteCertificate);
    const certificatesHaveLoaded = useAppSelector(getCertificatesHaveLoaded);

    const automaticallyPopModal = () => {
        dispatch(openTaxExemptModal());
        setModalPopped(true);
    };

    // If they go to the TaxExempt page dont pop the modal again during a session
    React.useEffect(() => {
        if (taxExemptPage) {
            setModalPopped(true);
        }
    }, [taxExemptPage]);

    React.useEffect(() => {
        if (
            isReseller &&
            certificatesHaveLoaded &&
            !hasValidTaxExemptCertificate &&
            !modalPopped &&
            !taxExemptPage &&
            !isSkippablePage
        ) {
            window.setTimeout(automaticallyPopModal, 2000);
        }
    }, [certificatesHaveLoaded, hasValidTaxExemptCertificate, isReseller, isSkippablePage, modalPopped, taxExemptPage]); // eslint-disable-line

    if (!isModalOpen) {
        return null;
    }

    let body = (
        <>
            <StyledSubtitleText secondary>
                <FormattedMessage id="taxExemptModal.subtitle" />
            </StyledSubtitleText>
            <StyledButtonContainer>
                <StyledDeleteButton
                    onClick={() => {
                        dispatch(dismissModal());
                        dispatch(push(getTaxCertManagementUrl()));
                    }}
                >
                    <FormattedMessage id="taxExemptModal.upload" />
                </StyledDeleteButton>
                <Button
                    onClick={() => {
                        dispatch(sendResellerType(0));
                        dispatch(dismissModal());
                    }}
                    secondary
                >
                    <FormattedMessage id="taxExemptModal.notExempt" />
                </Button>
            </StyledButtonContainer>
            {false && (
                <StyledQuestionContainer>
                    {/* this is temporarily disabled while the content is created */}
                    <FormattedMessage
                        id="taxExemptModal.notSure"
                        values={{
                            link: (
                                <Link to="www.google.com">
                                    <FormattedMessage id="taxExemptModal.learn" />
                                </Link>
                            ),
                        }}
                    />
                </StyledQuestionContainer>
            )}
        </>
    );

    return (
        <StyledModal
            body={body}
            contentLabel="taxexemptmodal"
            header={
                <ModalHeader
                    onClose={() => {
                        dispatch(dismissModal());
                    }}
                    title={<FormattedMessage id="taxExemptModal.title" />}
                />
            }
            show
        />
    );
};

export default TaxExemptModal;

const StyledSubtitleText = styled(Text)`
    display: block;
    margin-bottom: 50px;
    text-align: center;
`;

// TODO: Re-add this style to the container when we add back in the question link container
/* margin-bottom: 50px; */
const StyledButtonContainer = styled.div`
    text-align: center;
    display: flex;
    flex-direction: row;
    justify-content: center;

    button {
        min-width: 200px;
    }

    @media (max-width: 450px) {
        flex-direction: column;
    }
`;

const StyledDeleteButton = styled(Button)`
    && {
        min-width: 120px;
        margin-right: 10px;

        @media (max-width: 450px) {
            margin-right: 0;
            margin-bottom: 10px;
        }
    }
`;

const StyledModal = styled(Modal).attrs(() => ({
    bodyClassName: 'bodyClassName',
}))`
    .bodyClassName {
        padding: 60px;
        margin: 0 auto;
        width: 100%;

        @media (max-width: 767px) {
            padding: 30px 20px 40px;
        }
    }
`;

const StyledQuestionContainer = styled.div`
    display: block;
    text-align: center;
`;
